<template>
  <div class="tags-view">
    <!-- 页签滚动区 -->
    <div
      ref="scrollEl"
      class="tags-scroll"
    >
      <div
        v-for="tag in tags"
        :key="tag.path"
        class="tag-item"
        :class="{ 'is-active': tag.path === route.path }"
        :title="tag.fullPath"
        @click="activateTag(tag)"
      >
        <span class="tag-dot" />
        <span class="tag-title">{{ tag.title }}</span>
        <el-icon
          v-if="!tag.affix"
          class="tag-close"
          @click.stop="closeTag(tag)"
        >
          <Close />
        </el-icon>
      </div>
    </div>

    <!-- 批量操作 -->
    <el-dropdown
      class="tags-tools"
      trigger="click"
      @command="handleCommand"
    >
      <div class="tags-tools-btn">
        <el-icon :size="14">
          <ArrowDown />
        </el-icon>
      </div>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item command="closeOthers">
            关闭其它页签
          </el-dropdown-item>
          <el-dropdown-item command="closeAll">
            关闭全部页签
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'

/**
 * 页签项
 */
interface TagItem {
  /** 路由 path，作为页签唯一标识（同页面不同参数复用同一页签） */
  path: string
  /** 最近一次完整路径，用于激活跳转 */
  fullPath: string
  /** 展示标题 */
  title: string
  /** 是否固定（不可关闭） */
  affix: boolean
}

/** 首页固定页签 */
const HOME_TAG: TagItem = {
  path: '/dashboard',
  fullPath: '/dashboard',
  title: '监控大盘',
  affix: true,
}

const route = useRoute()
const router = useRouter()
const scrollEl = ref<HTMLElement>()
const tags = ref<TagItem[]>([])

/**
 * 当前路由是否需要展示页签。
 * 登录页等公开页、404 页不纳入页签。
 */
const shouldTrack = () =>
  !route.meta.public && route.name !== 'NotFound' && route.path !== '/login'

/**
 * 确保固定首页始终存在。
 */
function ensureHomeTag() {
  if (!tags.value.some(tag => tag.affix)) {
    tags.value.unshift({ ...HOME_TAG })
  }
}

/**
 * 将激活的页签滚动到可视区。
 */
function scrollToActive() {
  nextTick(() => {
    const activeEl = scrollEl.value?.querySelector<HTMLElement>('.tag-item.is-active')
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }
  })
}

/**
 * 路由变化时同步页签列表。
 * 以 path 为唯一标识：新页面追加，已存在页面仅刷新标题与完整路径。
 */
function syncTabs() {
  if (!shouldTrack()) {
    return
  }
  ensureHomeTag()

  const rawTitle = route.meta.title
  const title = typeof rawTitle === 'string' && rawTitle.trim()
    ? rawTitle.trim()
    : String(route.name || route.path)

  const index = tags.value.findIndex(tag => tag.path === route.path)
  if (index > -1) {
    tags.value[index].title = title
    tags.value[index].fullPath = route.fullPath
  } else {
    tags.value.push({ path: route.path, fullPath: route.fullPath, title, affix: false })
  }
  scrollToActive()
}

watch(() => route.fullPath, syncTabs, { immediate: true })

/**
 * 点击页签切换页面。
 */
function activateTag(tag: TagItem) {
  if (tag.fullPath !== route.fullPath) {
    router.push(tag.fullPath)
  }
}

/**
 * 关闭页签；若关闭的是当前页则自动切换到相邻页签。
 */
function closeTag(tag: TagItem) {
  if (tag.affix) {
    return
  }
  const index = tags.value.findIndex(item => item.path === tag.path)
  if (index === -1) {
    return
  }
  const isActive = tag.path === route.path
  tags.value.splice(index, 1)

  if (isActive) {
    const fallback = tags.value[index] || tags.value[index - 1] || tags.value[tags.value.length - 1]
    if (fallback) {
      router.push(fallback.fullPath)
    } else {
      router.push(HOME_TAG.path)
    }
  }
}

/**
 * 处理批量操作命令。
 */
function handleCommand(command: string) {
  if (command === 'closeOthers') {
    tags.value = tags.value.filter(tag => tag.affix || tag.path === route.path)
  } else if (command === 'closeAll') {
    tags.value = tags.value.filter(tag => tag.affix)
    if (!tags.value.some(tag => tag.path === route.path)) {
      router.push(HOME_TAG.path)
    }
  }
}
</script>

<style scoped lang="scss">
.tags-view {
  height: 40px;
  display: flex;
  align-items: stretch;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
  position: relative;
  z-index: 9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

  .tags-scroll {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 12px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .tag-item {
    display: flex;
    align-items: center;
    gap: 5px;
    height: 26px;
    padding: 0 10px;
    flex-shrink: 0;
    font-size: 13px;
    color: var(--text-secondary);
    background: #f5f6f8;
    border: 1px solid rgba(0, 0, 0, 0.04);
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1);

    .tag-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #c0c4cc;
      flex-shrink: 0;
      transition: background 0.25s ease;
    }

    .tag-title {
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .tag-close {
      width: 16px;
      height: 16px;
      font-size: 12px;
      color: var(--text-tertiary);
      border-radius: 50%;
      transition: all 0.2s ease;
      flex-shrink: 0;

      &:hover {
        color: #fff;
        background: #f56c6c;
      }
    }

    &:hover {
      background: #eceef2;
      color: var(--text-primary);

      .tag-dot {
        background: var(--text-tertiary);
      }
    }

    &.is-active {
      background: #ecf5ff;
      border-color: rgba(64, 158, 255, 0.25);
      color: var(--el-color-primary);
      font-weight: 500;

      .tag-dot {
        background: var(--el-color-primary);
        box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
      }

      .tag-close:hover {
        background: var(--el-color-primary);
      }
    }
  }

  .tags-tools {
    display: flex;
    align-items: center;
    flex-shrink: 0;

    .tags-tools-btn {
      width: 32px;
      height: 32px;
      margin-right: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-secondary);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f0f2ff;
        color: var(--el-color-primary);
      }
    }
  }
}
</style>
